import React, { createContext, useContext, useState, useRef, useCallback, ReactNode, useMemo } from 'react';
import { toast } from 'react-toastify';
import { VoiceInputIntegrationContext } from '../../../shared/designSystem/components/textField/hooks/useVoiceInputIntegration';

interface VoiceInputContextValue {
  isRecording: boolean;
  isVisible: boolean;
  activeInput: HTMLInputElement | HTMLTextAreaElement | null;
  registerInput: (element: HTMLInputElement | HTMLTextAreaElement) => void;
  unregisterInput: () => void;
  updateCaretPosition: (start: number, end: number) => void;
  insertTextAtCaret: (text: string) => void;
  startRecording: () => void;
  stopRecording: () => void;
  toggleRecording: () => void;
}

const VoiceInputContext = createContext<VoiceInputContextValue | undefined>(undefined);

export const useVoiceInput = () => {
  const context = useContext(VoiceInputContext);
  if (!context) {
    throw new Error('useVoiceInput must be used within VoiceInputProvider');
  }
  return context;
};

// Optional version that doesn't throw error - for use in shared components
export const useVoiceInputOptional = () => {
  const context = useContext(VoiceInputContext);
  return context;
};

interface VoiceInputProviderProps {
  children: ReactNode;
}

export const VoiceInputProvider: React.FC<VoiceInputProviderProps> = ({ children }) => {
  const [isRecording, setIsRecording] = useState(false);
  // Changed to true so button is always visible in SmartZone
  const [isVisible, setIsVisible] = useState(true);
  const activeInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const caretPositionRef = useRef({ start: 0, end: 0 });

  const registerInput = useCallback((element: HTMLInputElement | HTMLTextAreaElement) => {
    activeInputRef.current = element;
    setIsVisible(true);

    // Update caret position immediately
    if (element.selectionStart !== null && element.selectionEnd !== null) {
      caretPositionRef.current = {
        start: element.selectionStart,
        end: element.selectionEnd,
      };
    }
  }, []);

  const unregisterInput = useCallback(() => {
    activeInputRef.current = null;
    // Keep button visible even after blur
    // Auto-stop recording when input loses focus
    if (isRecording) {
      setIsRecording(false);
    }
  }, [isRecording]);

  const updateCaretPosition = useCallback((start: number, end: number) => {
    caretPositionRef.current = { start, end };
  }, []);

  const insertTextAtCaret = useCallback((text: string) => {
    const element = activeInputRef.current;
    if (!element) return;

    const { start, end } = caretPositionRef.current;

    // Use setRangeText for native text insertion
    element.setRangeText(text, start, end, 'end');

    // Dispatch input event to trigger React's onChange
    const inputEvent = new Event('input', { bubbles: true });
    element.dispatchEvent(inputEvent);

    // Update caret position to end of inserted text
    const newPosition = start + text.length;
    caretPositionRef.current = { start: newPosition, end: newPosition };

    // Focus the element to ensure caret is visible
    element.focus();
  }, []);

  const startRecording = useCallback(() => {
    if (activeInputRef.current) {
      setIsRecording(true);
    } else {
      toast.warning('Please click on a text field first before starting voice input.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    setIsRecording(false);
  }, []);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const value: VoiceInputContextValue = {
    isRecording,
    isVisible,
    activeInput: activeInputRef.current,
    registerInput,
    unregisterInput,
    updateCaretPosition,
    insertTextAtCaret,
    startRecording,
    stopRecording,
    toggleRecording,
  };

  // Create a simplified context for text field integration
  const integrationValue = useMemo(
    () => ({
      registerInput,
      unregisterInput,
      updateCaretPosition,
    }),
    [registerInput, unregisterInput, updateCaretPosition],
  );

  return (
    <VoiceInputContext.Provider value={value}>
      <VoiceInputIntegrationContext.Provider value={integrationValue}>{children}</VoiceInputIntegrationContext.Provider>
    </VoiceInputContext.Provider>
  );
};
